import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../../../components/DataGrid/DataGrid';
import EmptyState from '../../../../components/EmptyState';
import TourButton from '../../../../components/TourButton';
import { usePageTour } from '../../../../hooks/usePageTour';
import {
  salesInvoiceService,
  SalesInvoiceListDTO,
  SalesInvoiceStatus,
  PaymentStatus,
} from '../../../../services/salesInvoiceService';
import './SalesInvoicePage.css';
import { CirclePlus, Eye, RefreshCw, Smile, SquarePen, FileText, PenLine, AlertCircle, Check } from 'lucide-react';

// ─── Badge maps ───────────────────────────────────────────────────────────────

const STATUS_BADGE: Record<SalesInvoiceStatus, string> = {
  Draft:        'ims-badge ims-badge-draft',
  Issued:       'ims-badge ims-badge-submitted',
  PartiallyPaid:'ims-badge ims-badge-partially-received',
  Paid:         'ims-badge ims-badge-completed',
  Overdue:      'ims-badge ims-badge-rejected',
  Voided:       'ims-badge ims-badge-closed',
  Cancelled:    'ims-badge ims-badge-closed',
};

const PAYMENT_CLASS: Record<PaymentStatus, string> = {
  Unpaid:       'si-pay-unpaid',
  PartiallyPaid:'si-pay-partiallypaid',
  Paid:         'si-pay-paid',
  Voided:       'si-pay-voided',
};

// ─── Component ────────────────────────────────────────────────────────────────

const SalesInvoiceListPage: React.FC = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { t }     = useTranslation();

  const [invoices,       setInvoices]       = useState<SalesInvoiceListDTO[]>([]);
  const [loading,        setLoading]        = useState(false);
  const [refreshing,     setRefreshing]     = useState(false);
  const [error,          setError]          = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(
    (location.state as any)?.message || null
  );

  useEffect(() => { loadInvoices(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const loadInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await salesInvoiceService.getSalesInvoices({ pageNumber: 1, pageSize: 100 });
      const data = res.data?.data;
      if (Array.isArray(data)) setInvoices(data);
    } catch {
      setError(t('salesInvoice.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const res = await salesInvoiceService.getSalesInvoices({ pageNumber: 1, pageSize: 100 });
      const data = res.data?.data;
      if (Array.isArray(data)) setInvoices(data);
    } catch {
      setError(t('salesInvoice.failedToLoad'));
    } finally {
      setRefreshing(false);
    }
  };

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';

  const fmt = (n: number) =>
    new Intl.NumberFormat(langCode, { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

  // ── Stat counts ────────────────────────────────────────────────────────────
  const totalCount   = invoices.length;
  const draftCount   = invoices.filter((i) => i.status === 'Draft').length;
  const issuedCount  = invoices.filter((i) => i.status === 'Issued' || i.status === 'PartiallyPaid').length;
  const overdueCount = invoices.filter((i) => i.status === 'Overdue').length;

  const hasReferenceNumbers = invoices.some((i) => i.referenceNumber);

  // ── Columns ────────────────────────────────────────────────────────────────
  const columns: GridColumn[] = [
    {
      key: 'invoiceNumber',
      label: t('salesInvoice.invoiceNumber'),
      sortable: true,
      width: '150px',
      render: (value) => <code>{value}</code>,
    },
    ...(hasReferenceNumbers ? [{
      key: 'referenceNumber',
      label: t('salesInvoice.invoiceReferenceNumber'),
      sortable: true,
      width: '140px',
      hideMobile: true,
      render: (value: string | null | undefined) => value ? <code style={{ fontSize: 11 }}>{value}</code> : <span style={{ color: 'var(--ims-text-muted)' }}>—</span>,
    }] : []),
    {
      key: 'customerName',
      label: t('salesInvoice.customer'),
      sortable: true,
      width: '200px',
      render: (value) => <strong>{value}</strong>,
    },
    {
      key: 'invoiceDate',
      label: t('salesInvoice.invoiceDate'),
      sortable: true,
      width: '120px',
      render: (value) => new Date(value).toLocaleDateString(langCode),
    },
    {
      key: 'dueDate',
      label: t('salesInvoice.dueDate'),
      sortable: true,
      width: '120px',
      hideMobile: true,
      render: (value) => new Date(value).toLocaleDateString(langCode),
    },
    {
      key: 'status',
      label: t('common.status'),
      sortable: true,
      width: '120px',
      render: (value: SalesInvoiceStatus) => (
        <span className={STATUS_BADGE[value] ?? 'ims-badge ims-badge-draft'}>
          {t(`salesInvoice.status${value}`)}
        </span>
      ),
    },
    {
      key: 'paymentStatus',
      label: t('salesInvoice.paymentStatus'),
      sortable: true,
      width: '130px',
      hideMobile: true,
      render: (value: PaymentStatus) => (
        <span className={PAYMENT_CLASS[value] ?? 'si-pay-unpaid'}>
          {t(`salesInvoice.payment${value}`)}
        </span>
      ),
    },
    {
      key: 'totalAmount',
      label: t('salesInvoice.totalAmount'),
      sortable: true,
      width: '130px',
      render: (value) => <strong>{fmt(value)}</strong>,
    },
    {
      key: 'balanceDue',
      label: t('salesInvoice.balanceDue'),
      sortable: true,
      width: '120px',
      hideMobile: true,
      render: (value, row: SalesInvoiceListDTO) => (
        <span style={{ color: value > 0 ? 'var(--color-danger)' : 'var(--ims-green-text)', fontWeight: 600 }}>
          {fmt(value)}
        </span>
      ),
    },
  ];

  const actions: GridAction[] = [
    {
      icon: (
        <Eye size={18} strokeWidth={1.75} />
      ),
      onClick: (item: SalesInvoiceListDTO) => navigate(`/sales/sales-invoices/${item.id}`),
      className: 'grid-action-btn-view',
      title: t('common.view'),
    },
    {
      icon: (
        <SquarePen size={18} strokeWidth={1.75} />
      ),
      onClick: (item: SalesInvoiceListDTO) => navigate(`/sales/sales-invoices/${item.id}/edit`),
      className: 'grid-action-btn-edit',
      title: t('common.edit'),
      hidden: (item: SalesInvoiceListDTO) => item.status !== 'Draft',
    },
  ];

  const { startTour } = usePageTour('sales-invoices', [
    {
      element: '[data-tour="si-new"]',
      popover: {
        title: t('tour.salesInvoices.newTitle') || 'Make a sale',
        description: t('tour.salesInvoices.newText')
          || 'Every sale is an invoice. Click here to ring up items, take payment and print the bill.',
      },
    },
    {
      element: '[data-tour="si-stats"]',
      popover: {
        title: t('tour.salesInvoices.statsTitle') || "Today's numbers at a glance",
        description: t('tour.salesInvoices.statsText')
          || 'Totals update as you sell — paid, unpaid and overall amounts.',
      },
    },
    {
      element: '[data-tour="si-outstanding"]',
      popover: {
        title: t('tour.salesInvoices.outstandingTitle') || 'Credit customers',
        description: t('tour.salesInvoices.outstandingText')
          || 'See who still owes you money and settle their credit invoices here.',
      },
    },
    {
      element: '[data-tour="si-grid"]',
      popover: {
        title: t('tour.salesInvoices.gridTitle') || 'Invoice history',
        description: t('tour.salesInvoices.gridText')
          || 'Search past invoices, open one to reprint the bill or record a payment.',
      },
    },
  ], !loading);

  return (
    <Layout>
      <div className="si-page">

        {/* ── Header ── */}
        <div className="ims-list-header">
          <h1 className="ims-list-title">{t('salesInvoice.title')}</h1>
          <div className="ims-list-actions">
            <TourButton onClick={startTour} />
            <button
              className="btn-new"
              data-tour="si-new"
              onClick={() => navigate('/sales/sales-invoices/create')}
            >
              <CirclePlus size={18} strokeWidth={1.75} />
              {t('salesInvoice.createInvoice')}
            </button>
            <button
              className="ims-btn ims-btn-secondary"
              data-tour="si-outstanding"
              onClick={() => navigate('/sales/sales-invoices/outstanding')}
              title="View outstanding customer invoices"
            >
              <Smile size={18} strokeWidth={1.75} color="orange" />
              Outstanding Review
            </button>
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('common.refresh')}
            >
              <RefreshCw size={15} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('common.refresh')}
            </button>
          </div>
        </div>

        {/* ── Stats row ── */}
        <div className="ims-stats-row" data-tour="si-stats">
          <div className="ims-stat-card">
            <div className="ims-stat-icon ims-stat-icon-total">
              <FileText size={18} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{totalCount}</span>
              <span className="ims-stat-label">{t('salesInvoice.statTotal')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon ims-stat-icon-draft">
              <PenLine size={18} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{draftCount}</span>
              <span className="ims-stat-label">{t('salesInvoice.statusDraft')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon ims-stat-icon-approved">
              <FileText size={18} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{issuedCount}</span>
              <span className="ims-stat-label">{t('salesInvoice.statIssued')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon ims-stat-icon-danger">
              <AlertCircle size={18} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{overdueCount}</span>
              <span className="ims-stat-label">{t('salesInvoice.statusOverdue')}</span>
            </div>
          </div>
        </div>

        {/* ── Alerts ── */}
        {successMessage && (
          <div className="ims-alert ims-alert-success" style={{ marginBottom: 16 }}>
            <Check size={16} strokeWidth={2} style={{ flexShrink: 0 }} />
            <span>{successMessage}</span>
            <button
              type="button"
              onClick={() => setSuccessMessage(null)}
              style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}
            >✕</button>
          </div>
        )}
        {error && (
          <div className="ims-alert ims-alert-error" style={{ marginBottom: 16 }}>
            <AlertCircle size={16} strokeWidth={2} style={{ flexShrink: 0 }} />
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setError(null)}
              style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}
            >✕</button>
          </div>
        )}

        {/* ── DataGrid ── */}
        <div className="ims-grid-wrapper" data-tour="si-grid">
          <DataGrid
            data={invoices}
            columns={columns}
            actions={actions}
            loading={loading}
            pageSize={10}
            searchPlaceholder={t('common.search')}
            emptyMessage={t('salesInvoice.noInvoices')}
            emptyState={(
              <EmptyState
                icon={<FileText size={44} strokeWidth={1.2} />}
                title={t('emptyState.salesInvoices.title') || 'No sales yet'}
                description={t('emptyState.salesInvoices.text')
                  || 'Every sale you make becomes an invoice here — with its payments, balance and printable bill. Create your first invoice to get started.'}
                actionLabel={t('salesInvoice.createInvoice')}
                onAction={() => navigate('/sales/sales-invoices/create')}
              />
            )}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </div>

      </div>
    </Layout>
  );
};

export default SalesInvoiceListPage;
